import React, { useState, useRef, useEffect } from "react";

/*
  Editeur Newsletter (React + Tailwind, sans dépendances)
  - 3 colonnes responsive (gauche: structure, centre: aperçu 50%, droite: ajout/édition)
  - Blocs pris en charge: texte, image (jpeg,tiff,png,pdf...), vidéo, bouton (cliquable), fichier (Word, PDF...), lien en ligne, réseaux sociaux, RGPD
  - Branding: upload logo + champ police (nom) pour indiquer l'intégration de typographies
  - Templates: sauvegarde / chargement / liste (localStorage)
  - Aperçu live centré et imprimable (export PDF via fenêtre d'impression)
  - Envoi test: ouvre nouvelle fenêtre avec rendu + paramètres de tracking simulés
  - Tracking & relance: toggles et insertion d'un paramètre de tracking dans les liens
  - Ergonomie minimale (toolbar texte riche via contentEditable)

  Note: Ceci est une POC front-end. Pour la diffusion réelle, intégration serveur/API d'emailing, CDN pour les assets et gestion RGPD côté back-end sont nécessaires.
*/

export const EditeurNewsletterVersailles = () => {
    const [blocks, setBlocks] = useState([]);
    const [selectedBlock, setSelectedBlock] = useState(null);
    const [branding, setBranding] = useState({ logo: null, fontName: "" });
    const [trackingEnabled, setTrackingEnabled] = useState(true);
    const [autoRelance, setAutoRelance] = useState(false);
    const [templates, setTemplates] = useState([]);
    const templateNameRef = useRef();

    useEffect(() => {
        // charger templates depuis localStorage
        const raw = localStorage.getItem("ens_templates_v1");
        if (raw) {
            try {
                setTemplates(JSON.parse(raw));
            } catch (e) {
                console.warn("Templates non valides");
            }
        }
    }, []);

    useEffect(() => {
        // sauvegarde automatique des templates list dans localStorage
        localStorage.setItem("ens_templates_v1", JSON.stringify(templates));
    }, [templates]);

    // --- Blocs ---
    function addBlock(type) {
        const id = Date.now() + Math.random();
        const base = { id, type };
        let block = base;
        switch (type) {
            case "text":
                block = { ...base, content: "<p>Entrez votre texte...</p>" };
                break;
            case "image":
                block = { ...base, file: null, alt: "" };
                break;
            case "video":
                block = { ...base, src: "" };
                break;
            case "button":
                block = { ...base, label: "Cliquez ici", href: "https://" };
                break;
            case "file":
                block = { ...base, file: null };
                break;
            case "link":
                block = { ...base, href: "https://", label: "Lien" };
                break;
            case "social":
                block = { ...base, networks: ["facebook", "twitter", "linkedin"] };
                break;
            case "rgpd":
                block = { ...base, href: "#rgpd" };
                break;
            default:
                break;
        }
        setBlocks((b) => [...b, block]);
        setSelectedBlock(id);
    }

    function updateBlock(id, patch) {
        setBlocks((prev) => prev.map((b) => (b.id === id ? { ...b, ...patch } : b)));
    }

    function removeBlock(id) {
        setBlocks((prev) => prev.filter((b) => b.id !== id));
        if (selectedBlock === id) setSelectedBlock(null);
    }

    function handleFileUpload(e, blockId, forType = "image") {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        const url = URL.createObjectURL(file);
        if (forType === "image" || forType === "file") {
            updateBlock(blockId, { file: { name: file.name, url, type: file.type } });
        }
        e.target.value = "";
    }

    // Branding upload
    function handleLogoUpload(e) {
        const f = e.target.files && e.target.files[0];
        if (!f) return;
        const url = URL.createObjectURL(f);
        setBranding((b) => ({ ...b, logo: { name: f.name, url } }));
        e.target.value = "";
    }

    // Templates
    function saveTemplateLocal() {
        const name = templateNameRef.current?.value || `template-${Date.now()}`;
        const payload = { id: Date.now(), name, branding, blocks, savedAt: new Date().toISOString() };
        setTemplates((t) => [payload, ...t]);
        if (templateNameRef.current) templateNameRef.current.value = "";
        alert("Template enregistré localement.");
    }

    function loadTemplateToEditor(tpl) {
        setBranding(tpl.branding || { logo: null, fontName: "" });
        setBlocks(tpl.blocks || []);
        setSelectedBlock(null);
    }

    function deleteTemplate(id) {
        setTemplates((t) => t.filter((x) => x.id !== id));
    }

    // Export PDF (ouvre une fenêtre imprimable)
    function exportPDF() {
        const win = window.open("", "_blank");
        const html = `<!doctype html><html><head><meta charset='utf-8'><title>Export - Newsletter</title><style>body{font-family:Arial,Helvetica,sans-serif;padding:24px;}img{max-width:100%;height:auto;}a{color:#0b5;}</style></head><body>${renderPreviewHtml(true)}<script>window.print();</script></body></html>`;
        win.document.write(html);
        win.document.close();
    }

    // Envoi test (simulé): ouvre fenêtre avec rendu + tracking params
    function sendTest() {
        const win = window.open("", "_blank");
        const trackingParam = trackingEnabled ? `?trk=test_${Date.now()}` : "";
        const html = `<!doctype html><html><head><meta charset='utf-8'><title>Envoi test</title><style>body{font-family:Arial,Helvetica,sans-serif;padding:24px;}img{max-width:100%;}</style></head><body>${renderPreviewHtml(true, trackingParam)}</body></html>`;
        win.document.write(html);
        win.document.close();
        alert("Fenêtre d'envoi test ouverte. Vérifiez les liens (paramètre trk ajouté si le tracking est activé).");
    }

    // Render HTML used both for preview and for print/test
    function renderPreviewHtml(forPrint = false, trackingParam = "") {
        // small helper to add tracking param to hrefs
        const addTrack = (href) => {
            if (!href) return "#";
            if (!trackingEnabled) return href;
            return href.includes("?") ? `${href}&trk=1` : `${href}?trk=1`;
        };

        const html = blocks
            .map((b) => {
                switch (b.type) {
                    case "text":
                        return `<div class='block-text'>${b.content}</div>`;
                    case "image":
                        if (!b.file) return "";
                        // support PDF preview as link
                        if (b.file.type === "application/pdf") {
                            return `<div class='block-image'><a href='${b.file.url}${trackingParam}' target='_blank' rel='noreferrer'>Ouvrir le PDF (${b.file.name})</a></div>`;
                        }
                        return `<div class='block-image'><img src='${b.file.url}' alt='${b.alt || b.file.name}' /></div>`;
                    case "video":
                        return b.src ? `<div class='block-video'><iframe src='${b.src}' width='100%' height='320' frameborder='0' allowfullscreen></iframe></div>` : "";
                    case "button":
                        return `<div class='block-button'><a href='${addTrack(b.href)}' style='display:inline-block;padding:10px 16px;border-radius:6px;background:#1f2937;color:#fff;text-decoration:none;'>${escapeHtml(
                            b.label
                        )}</a></div>`;
                    case "file":
                        return b.file ? `<div class='block-file'><a href='${b.file.url}' download='${b.file.name}'>Télécharger ${escapeHtml(b.file.name)}</a></div>` : "";
                    case "link":
                        return `<div class='block-link'><a href='${addTrack(b.href)}' target='_blank' rel='noreferrer'>${escapeHtml(b.label)}</a></div>`;
                    case "social":
                        // simple social share links
                        return `<div class='block-social'>${(b.networks || []).map((n) => socialLinkMarkup(n, trackingParam)).join(" ")}</div>`;
                    case "rgpd":
                        return `<div class='block-rgpd'><a href='${b.href}'>Lien RGPD</a></div>`;
                    default:
                        return "";
                }
            })
            .join("\n");

        // wrap with branding header if present (logo)
        const logoHtml = branding.logo ? `<div style="margin-bottom:12px;"><img src='${branding.logo.url}' alt='logo' style='height:48px;'/></div>` : "";

        return `${logoHtml}<div class='newsletter-root'>${html}</div>`;
    }

    function socialLinkMarkup(network, trackingParam) {
        const urlMap = {
            facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href + trackingParam)}`,
            twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href + trackingParam)}`,
            linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href + trackingParam)}`,
        };
        const label = network.charAt(0).toUpperCase() + network.slice(1);
        return `<a href='${urlMap[network] || "#"}' target='_blank' rel='noreferrer' style='margin-right:8px;'>${label}</a>`;
    }

    function escapeHtml(str) {
        if (!str) return "";
        return String(str)
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/\"/g, "&quot;")
            .replace(/'/g, "&#039;");
    }

    // small reorder helpers
    function moveBlock(id, dir) {
        setBlocks((prev) => {
            const idx = prev.findIndex((p) => p.id === id);
            if (idx === -1) return prev;
            const newIdx = dir === "up" ? idx - 1 : idx + 1;
            if (newIdx < 0 || newIdx >= prev.length) return prev;
            const copy = [...prev];
            const [item] = copy.splice(idx, 1);
            copy.splice(newIdx, 0, item);
            return copy;
        });
    }

    return (
        <div className="min-h-full bg-gray-50 p-4">
            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* gauche - structure & templates */}
                <aside className="lg:col-span-3 bg-white rounded-2xl p-4 shadow-sm">
                    <h3 className="text-lg font-semibold mb-3">Structure & Templates</h3>

                    <div className="mb-3">
                        <h4 className="text-sm font-medium">Blocs</h4>
                        <ul className="space-y-2 mt-2">
                            {blocks.length === 0 && <li className="text-sm text-gray-500">Aucun bloc</li>}
                            {blocks.map((b, idx) => (
                                <li key={b.id} className="flex items-center justify-between p-2 border rounded-sm">
                                    <div className="text-sm">{idx + 1}. {b.type}</div>
                                    <div className="flex items-center gap-2">
                                        <button onClick={() => moveBlock(b.id, "up")} className="text-xs px-2 py-1 border rounded-sm">↑</button>
                                        <button onClick={() => moveBlock(b.id, "down")} className="text-xs px-2 py-1 border rounded-sm">↓</button>
                                        <button onClick={() => setSelectedBlock(b.id)} className="text-xs px-2 py-1 border rounded-sm">✎</button>
                                        <button onClick={() => removeBlock(b.id)} className="text-xs px-2 py-1 border rounded-sm text-red-500">✕</button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="mb-3">
                        <h4 className="text-sm font-medium">Templates</h4>
                        <div className="flex gap-2 mt-2">
                            <input ref={templateNameRef} placeholder="Nom du template" className="flex-1 border rounded-sm px-2 py-1 text-sm" />
                            <button onClick={saveTemplateLocal} className="px-3 py-1 bg-sky-600 text-white rounded-sm text-sm">Enregistrer</button>
                        </div>
                        <div className="mt-3 space-y-2 max-h-40 overflow-auto">
                            {templates.length === 0 && <div className="text-sm text-gray-500">Aucun template</div>}
                            {templates.map((t) => (
                                <div key={t.id} className="flex items-center justify-between p-2 border rounded-sm">
                                    <div className="text-sm">{t.name}</div>
                                    <div className="flex gap-2">
                                        <button onClick={() => loadTemplateToEditor(t)} className="text-xs px-2 py-1 border rounded-sm">Charger</button>
                                        <button onClick={() => deleteTemplate(t.id)} className="text-xs px-2 py-1 border rounded-sm text-red-500">Suppr</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="mt-4">
                        <label className="flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={trackingEnabled} onChange={(e) => setTrackingEnabled(e.target.checked)} /> Activer tracking
                        </label>
                        <label className="flex items-center gap-2 text-sm mt-1">
                            <input type="checkbox" checked={autoRelance} onChange={(e) => setAutoRelance(e.target.checked)} /> Activer relance
                        </label>
                    </div>

                    <div className="mt-4">
                        <label className="block text-sm font-medium">Logo / Police</label>
                        <input type="file" accept="image/*" onChange={handleLogoUpload} className="mt-2" />
                        <input value={branding.fontName} onChange={(e) => setBranding((b) => ({ ...b, fontName: e.target.value }))} placeholder="Nom de la police" className="mt-2 w-full border px-2 py-1 rounded-sm text-sm" />
                        {branding.logo && <img src={branding.logo.url} alt="logo" className="h-10 mt-2" />}
                    </div>

                    <div className="mt-4">
                        <button onClick={sendTest} className="w-full bg-emerald-600 text-white rounded-sm py-2">Envoyer test</button>
                        <button onClick={exportPDF} className="w-full mt-2 bg-purple-600 text-white rounded-sm py-2">Exporter PDF</button>
                    </div>
                </aside>

                {/* centre - preview 50% width on desktop */}
                <main className="lg:col-span-6 bg-white rounded-2xl shadow-sm p-6 flex flex-col items-center">
                    <h3 className="text-lg font-semibold mb-3">Aperçu en direct</h3>
                    <div id="previewArea" className="w-full lg:w-3/4 overflow-auto" style={{ fontFamily: branding.fontName || 'inherit' }} dangerouslySetInnerHTML={{ __html: renderPreviewHtml() }} />

                    <div className="mt-4 text-xs text-gray-500">Liens de partage, RGPD, fichiers, vidéos et images sont pris en charge. Pour diffusion réelle, connectez un back-end.</div>
                </main>

                {/* droite - ajout & édition */}
                <aside className="lg:col-span-3 bg-white rounded-2xl p-4 shadow-sm">
                    <h3 className="text-lg font-semibold mb-3">Ajout & Édition</h3>

                    <div className="flex flex-wrap gap-2 mb-3">
                        <button onClick={() => addBlock("text")} className="px-3 py-1 rounded-sm bg-indigo-600 text-white">Texte</button>
                        <button onClick={() => addBlock("image")} className="px-3 py-1 rounded-sm bg-green-600 text-white">Photo / PDF</button>
                        <button onClick={() => addBlock("video")} className="px-3 py-1 rounded-sm bg-red-600 text-white">Vidéo</button>
                        <button onClick={() => addBlock("button")} className="px-3 py-1 rounded-sm bg-yellow-500 text-white">Bouton</button>
                        <button onClick={() => addBlock("file")} className="px-3 py-1 rounded-sm bg-gray-700 text-white">Fichier</button>
                        <button onClick={() => addBlock("link")} className="px-3 py-1 rounded-sm bg-sky-600 text-white">Lien</button>
                        <button onClick={() => addBlock("social")} className="px-3 py-1 rounded-sm bg-pink-600 text-white">Réseaux</button>
                        <button onClick={() => addBlock("rgpd")} className="px-3 py-1 rounded-sm bg-neutral-600 text-white">RGPD</button>
                    </div>

                    {selectedBlock ? (
                        <BlockEditor block={blocks.find((b) => b.id === selectedBlock)} updateBlock={updateBlock} handleFileUpload={handleFileUpload} />
                    ) : (
                        <div className="text-sm text-gray-500">Sélectionnez un bloc à éditer</div>
                    )}
                </aside>
            </div>
        </div>
    );
}

function BlockEditor({ block, updateBlock, handleFileUpload }) {
    if (!block) return null;
    return (
        <div className="space-y-3">
            <div className="text-sm font-medium mb-2">Édition — {block.type}</div>

            {block.type === "text" && <TextEditor block={block} updateBlock={updateBlock} />}

            {block.type === "image" && (
                <div>
                    <input type="file" accept="image/*,application/pdf,image/tiff" onChange={(e) => handleFileUpload(e, block.id, "image")} />
                    {block.file && (
                        <div className="mt-2">
                            {block.file.type === "application/pdf" ? (
                                <a href={block.file.url} target="_blank" rel="noreferrer">Ouvrir le PDF ({block.file.name})</a>
                            ) : (
                                <img src={block.file.url} alt={block.file.name} className="h-28 rounded-sm" />
                            )}
                            <input value={block.alt} onChange={(e) => updateBlock(block.id, { alt: e.target.value })} placeholder="Texte alternatif" className="mt-2 w-full border px-2 py-1 rounded-sm text-sm" />
                        </div>
                    )}
                </div>
            )}

            {block.type === "video" && (
                <div>
                    <input value={block.src || ""} onChange={(e) => updateBlock(block.id, { src: e.target.value })} placeholder="URL embed YouTube / iframe ou MP4" className="w-full border px-2 py-1 rounded-sm text-sm" />
                </div>
            )}

            {block.type === "button" && (
                <div>
                    <input value={block.label} onChange={(e) => updateBlock(block.id, { label: e.target.value })} className="w-full border px-2 py-1 rounded-sm text-sm mb-2" />
                    <input value={block.href} onChange={(e) => updateBlock(block.id, { href: e.target.value })} className="w-full border px-2 py-1 rounded-sm text-sm" />
                </div>
            )}

            {block.type === "file" && (
                <div>
                    <input type="file" onChange={(e) => handleFileUpload(e, block.id, "file")} />
                    {block.file && <div className="mt-2 text-sm">Fichier prêt: {block.file.name}</div>}
                </div>
            )}

            {block.type === "link" && (
                <div>
                    <input value={block.label} onChange={(e) => updateBlock(block.id, { label: e.target.value })} className="w-full border px-2 py-1 rounded-sm text-sm mb-2" />
                    <input value={block.href} onChange={(e) => updateBlock(block.id, { href: e.target.value })} className="w-full border px-2 py-1 rounded-sm text-sm" />
                </div>
            )}

            {block.type === "social" && (
                <div className="text-sm">
                    <label className="block">Réseaux (séparés par virgule)</label>
                    <input
                        value={(block.networks || []).join(",")}
                        onChange={(e) => updateBlock(block.id, { networks: e.target.value.split(",").map((s) => s.trim()) })}
                        className="w-full border px-2 py-1 rounded-sm text-sm"
                    />
                </div>
            )}

            {block.type === "rgpd" && (
                <div>
                    <input value={block.href} onChange={(e) => updateBlock(block.id, { href: e.target.value })} className="w-full border px-2 py-1 rounded-sm text-sm" />
                </div>
            )}
        </div>
    );
}

function TextEditor({ block, updateBlock }) {
    const ref = useRef(null);

    const exec = (cmd, value = null) => {
        document.execCommand(cmd, false, value);
    };

    useEffect(() => {
        // reposition caret at end when selecting block
        if (ref.current) {
            ref.current.focus();
        }
    }, [block.id]);

    return (
        <div>
            <div className="flex gap-2 mb-2">
                <button onClick={() => exec("bold")} className="px-2 py-1 border rounded-sm">B</button>
                <button onClick={() => exec("italic")} className="px-2 py-1 border rounded-sm">I</button>
                <button
                    onClick={() => {
                        const url = prompt("Entrez l'URL:", "https://");
                        if (url) exec("createLink", url);
                    }}
                    className="px-2 py-1 border rounded-sm"
                >
                    Lien
                </button>
            </div>
            <div ref={ref} contentEditable suppressContentEditableWarning className="border rounded-sm p-2 min-h-[120px] text-sm" onInput={(e) => updateBlock(block.id, { content: e.currentTarget.innerHTML })} dangerouslySetInnerHTML={{ __html: block.content }} />
        </div>
    );
}
